import './styles.scss';

import { Button, Loader } from '@ets-global/b2c-website-ui';
import classNames from 'classnames';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import SessionSearchBanner from '../../../components/Base/Banner/SessionSearchBanner';
import NotFoundHandler from '../../../components/NotFoundHandler';
import { PAYMENT_DETAIL } from '../../../graphql/queries';
import { CANCELLED_PURCHASE, CONFIRMED_PURCHASE } from '../../../graphql/queries/purchase';
import { CANCEL_PAYMENT } from '../../../graphql/mutations';
import { useQuery, useRouter } from '../../../hooks';
import { paths } from '../../../paths';
import { useMutation } from '@apollo/client';

const FailedPurchase = () => {
    const { t } = useTranslation();
    const { currentRoute: { params: { registrationId } } } = useRouter();

    const [cancelPayment, { loading: cancelPaymentInProgress }] = useMutation(CANCEL_PAYMENT, {
        refetchQueries: [
            { query: CONFIRMED_PURCHASE, variables: { registrationId } },
            { query: CANCELLED_PURCHASE, variables: { registrationId } },
            { query: PAYMENT_DETAIL, variables: { registrationId } }
        ],
        awaitRefetchQueries: true,
    });

    const {
        loading: loadingConfirmedPurchase,
        data: { confirmedPurchase } = {},
    } = useQuery(CONFIRMED_PURCHASE, { variables: { registrationId } });

    const {
        loading: loadingCancelledPurchase,
        data: { cancelledPurchase } = {},
    } = useQuery(CANCELLED_PURCHASE, { variables: { registrationId } });

    const {
        loading: paymentDetailLoading,
        data: { paymentDetail } = {},
    } = useQuery(PAYMENT_DETAIL, { variables: { registrationId } });

    const purchaseId = confirmedPurchase?.id || cancelledPurchase?.id;

    const handleCancelPayment = (registrationId) => () => {
        cancelPayment({ variables: { registrationId } });
    };

    const redirectToPayment = () => {
        window.location.assign(paymentDetail.paymentLink);
    };

    if (!loadingConfirmedPurchase && !purchaseId && !loadingCancelledPurchase && !cancelPaymentInProgress) {
        return <NotFoundHandler/>;
    }

    const renderContent = () => {
        if (loadingConfirmedPurchase || loadingCancelledPurchase) {
            return (
                <div className="account-order__loading">
                    <Loader/>
                </div>
            );
        }

        return (
            <div className={classNames('checkout-confirmation container container--full container--blue')}>
                <div className="container container--padding">
                    <h3 className="small-title">
                        <Trans>purchase-finalisation.purchase.fail.title</Trans>
                    </h3>
                    <div className="checkout-confirmation__block">
                        <p>
                            <Trans
                                values={{ purchaseNumber: purchaseId }}>purchase-finalisation.purchase.fail.content</Trans>
                        </p>
                        {renderActions()}
                    </div>
                </div>
            </div>
        );
    };

    const renderActions = () => {
        if (paymentDetailLoading || cancelPaymentInProgress) {
            return <Loader/>;
        }

        if (cancelledPurchase?.id) {
            return (
                <Button to={paths.SESSION_SEARCH}>
                    <Trans>session-registration.find-another-session</Trans>
                </Button>
            );
        }

        if (!!purchaseId && paymentDetail?.paymentLink) {
            return (
                <div className="checkout-confirmation__actions">
                    <Button
                        onClick={handleCancelPayment(registrationId)}
                        className="button--transparent-light"
                        data-cypress="buying-process.cancel-purchase"
                    >
                        <Trans>session-summary.purchase.cancel_button</Trans>
                    </Button>
                    <Button
                        onClick={redirectToPayment}
                        data-cypress="buying-process.payment-link"
                    >
                        <Trans>session-summary.purchase.finalize_button</Trans>
                    </Button>
                </div>
            );
        }

        return null;
    };

    return (
        <main className="main main--no-offset-top">
            <SessionSearchBanner/>
            {renderContent()}
        </main>
    );
};

export default FailedPurchase;

